/*
 * Copyright (c) 2021 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import makeStyles from '@material-ui/styles/makeStyles'
import { Box, Collapse, IconButton, Paper } from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import InfoIcon from '../images/icons/Info-Italic.svg'
import WarningIcon from '@material-ui/icons/Warning'
import clsx from 'clsx'
import Icon from '@mdi/react'
import { mdiAlertBox } from '@mdi/js'
import Typography from '@material-ui/core/Typography'

const successColor = 'rgb(18, 129, 41)'
const errorColor = 'rgb(196, 32, 20)'
const warningColor = 'rgb(198, 127, 0)'
const infoColor = 'rgb(39, 57, 177)'

const useCustomStyles = makeStyles((theme) => ({
  success: {
    borderColor: successColor,
  },
  error: {
    borderColor: errorColor,
  },
  warning: {
    borderColor: warningColor,
  },
  info: {
    borderColor: infoColor,
  },
  flexContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    borderStyle: 'solid',
    boxShadow: '0 2px 4px 0 rgba(109,109,109,0.53)',
    borderRadius: '3px',
    transition: 'background-color 100ms linear',
    justifyContent: 'space-between',
  },
  pageAlertBorder: {
    borderWidth: '1px',
    borderLeftWidth: '11px',
  },
  sectionAlertBorder: {
    borderWidth: 0,
    borderLeftWidth: '11px',
    backgroundColor: theme.palette.background.default,
  },
  notificationHeader: {
    ...theme.typography.h6,
    fontWeight: 'bold',
    paddingBottom: theme.spacing(1),
  },
  inlineErrorText: {
    color: errorColor,
    paddingLeft: theme.spacing(0.5),
  },
  inlineNotificationHeader: {
    ...theme.typography.h6,
    fontWeight: 'bold',
    marginTop: '4px',
  },
  inlineIcon: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(0.5),
  },
}))

/**
 * Alert Notification component
 * @param {String} variant - variant can be success/error/warning
 * @param {String|Node} header - alert header
 * @param {Node} children - alert message
 * @param {string} alertLevel - page level of the alert (page, section, inline)
 * @returns {Node} React node.
 * @constructor
 */
function AlertNotification({ id, variant, header, children: message, alertLevel = 'page', ...otherProps }) {
  const [open, toggle] = useState(true)
  const classes = useCustomStyles()
  const showCloseIcon = alertLevel === 'page' && variant !== 'error'

  /**
   * Returns the colors, border styles and icon to be used in alert
   * @returns {Object} - classname and icon image
   */
  const alertType = (size = 1) => {
    if (variant === 'success') {
      return { className: classes.success, icon: <CheckCircleIcon htmlColor={successColor} /> }
    } else if (variant === 'error') {
      return { className: classes.error, icon: <Icon path={mdiAlertBox} size={size} color={errorColor} /> }
    } else if (variant === 'warning') {
      return { className: classes.warning, icon: <WarningIcon htmlColor={warningColor} /> }
    } else {
      return { className: classes.info, icon: <InfoIcon fill={infoColor} height='22px' width='22px' /> }
    }
  }

  /**
   * Toggle alert notification
   * @type {Function}
   */
  const toggleAlert = useCallback(() => {
    toggle(false)
  }, [toggle])

  return alertLevel === 'inline' ? (
    <Box className={classes.inlineIcon} role='alert' data-testid={id}>
      <Box minWidth='20px' height='20px'>
        {alertType(0.75).icon}
      </Box>
      <Typography
        data-testid={`${id}-message`}
        variant='caption'
        className={variant === 'error' ? classes.inlineErrorText : null}>
        {message}
      </Typography>
    </Box>
  ) : (
    <Collapse in={open}>
      <Paper
        className={`${clsx(
          classes.flexContainer,
          alertType().className,
          alertLevel === 'page' ? classes.pageAlertBorder : classes.sectionAlertBorder,
        )}`}
        role='alert'
        data-testid={id}
        {...otherProps}>
        <Box px={1} py={1} display='flex'>
          <Box pr={1} display='flex'>
            {alertType().icon}
          </Box>
          <Box display='flex' justifyContent='center' flexDirection='column'>
            {header && (
              <strong data-testid={`${id}-header`} className={classes.notificationHeader}>
                {header}
              </strong>
            )}
            <div data-testid={`${id}-message`}>
              <Typography>{message}</Typography>
            </div>
          </Box>
        </Box>
        {showCloseIcon && (
          <Box>
            <IconButton data-testid={`${id}-dismiss`} onClick={toggleAlert} aria-label='Dismiss'>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </Paper>
    </Collapse>
  )
}

AlertNotification.propTypes = {
  id: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node.isRequired,
  showCloseIcon: PropTypes.bool,
  alertLevel: PropTypes.oneOf(['page', 'section', 'inline']),
}
export default AlertNotification
